/**
 * The four-legged frame the stag and the boar are both built on (M2-05).
 *
 * The wolf plan predates this and is written out longhand, deliberately: M1-01
 * needed to prove the schema survives a body that is not a man's, and a proof
 * written through a helper proves the helper. What the helper is for is the
 * *third* and fourth quadrupeds, where writing fourteen parts out by hand again
 * would be fourteen more chances to fat-finger a mobility share and find out
 * about it in a balance run.
 *
 * Everything a species actually differs on is a parameter: how much of its
 * consciousness is in its skull, how much weight is on the forelegs, and what
 * the parts are *called* — because a stag has hooves and slots, a boar has
 * trotters and a snout, and the log should say so.
 */
/**
 * `near` and `off` rather than `left` and `right`: the words a person standing
 * beside the animal would use, and the words the log reads better in.
 */
function side(which) {
    return which === 'left' ? 'near' : 'off';
}
function legs(shape) {
    const parts = [];
    for (const which of ['left', 'right']) {
        parts.push({
            id: `foreleg.${which}`,
            zone: 'leg',
            side: which,
            name: `${side(which)} ${shape.foreleg}`,
            hitWeight: shape.foreWeight,
            contributes: { mobility: 0.15 },
            contains: ['bone', 'muscle', 'joint'],
        });
    }
    for (const which of ['left', 'right']) {
        parts.push({
            id: `hindleg.${which}`,
            zone: 'leg',
            side: which,
            name: `${side(which)} ${shape.hindleg}`,
            hitWeight: 1,
            contributes: { mobility: 0.15 },
            contains: ['bone', 'muscle', 'joint'],
        });
    }
    for (const [end, weight] of [
        ['fore', shape.foreWeight],
        ['hind', 1],
    ]) {
        for (const which of ['left', 'right']) {
            parts.push({
                id: `${shape.foot}.${end}.${which}`,
                zone: 'foot',
                side: which,
                name: `${side(which)} ${end} ${shape.foot}`,
                hitWeight: weight,
                contributes: { mobility: 0.1 },
                contains: ['bone', 'tendon'],
            });
        }
    }
    return parts;
}
/**
 * Build a quadruped. The capability shares are fixed across species on
 * purpose — four legs pooling to one mobility is a fact about having four
 * legs, not about being a boar — so `validateBodyPlan` has one arithmetic to
 * check rather than one per animal.
 */
export function quadruped(shape) {
    return {
        id: shape.id,
        name: shape.name,
        parts: [
            {
                id: 'head',
                zone: 'head',
                side: null,
                name: 'head',
                hitWeight: 1,
                contributes: { consciousness: shape.skull },
                contains: ['skull', 'brain', 'jaw'],
                vital: true,
            },
            {
                id: 'eye.left',
                zone: 'eye',
                side: 'left',
                name: 'left eye',
                hitWeight: 1,
                contributes: { sight: 0.5 },
                contains: ['eye'],
            },
            {
                id: 'eye.right',
                zone: 'eye',
                side: 'right',
                name: 'right eye',
                hitWeight: 1,
                contributes: { sight: 0.5 },
                contains: ['eye'],
            },
            {
                id: 'neck',
                zone: 'neck',
                side: null,
                name: 'throat',
                hitWeight: 1,
                contributes: { consciousness: 1 - shape.skull, breath: 0.2 },
                contains: ['artery', 'windpipe', 'spine'],
                vital: true,
            },
            {
                id: 'chest',
                zone: 'chest',
                side: null,
                name: 'chest',
                hitWeight: 1,
                contributes: { breath: 0.6 },
                contains: ['rib', 'lung', 'heart'],
                vital: true,
            },
            {
                id: 'abdomen',
                zone: 'abdomen',
                side: null,
                name: 'flank',
                hitWeight: 1,
                contributes: { breath: 0.2 },
                contains: ['gut', 'liver'],
                vital: true,
            },
            ...legs(shape),
            ...(shape.extras ?? []),
        ],
    };
}
//# sourceMappingURL=quadruped.js.map